import {IStandardPagedResponse, IStandardResponse, TCompanyId} from "../../../common/types";
import {AxiosResponse} from "axios";
import {apiClient, createRetryingNotificationHandler, RetryConfig} from "../../../common/services/apiClient";
import {IProtocolDefStats} from "../types/IProtocolDefStats";
import {IProtocolDefStatsDTO} from "../types/dtos/IProtocolDefStatsDTO";
import dayjs from "dayjs";
import {TProtocolDefinitionId} from "../../types/protocolDefinitions";
import {IProtocolStatsDTO} from "../types/dtos/IProtocolStats";
import {IProtocolStats} from "../types/IProtocolStats";
import {IProtocol} from "../../types/protocol";
import {
    ProtocolFigureHrHrv,
    ProtocolFigureRmsSnr,
    ProtocolFigureSpikeCounts,
    ProtocolFigureSpikes,
    ProtocolFigureDeltaConn,
    ProtocolFigureConnDist,
    ProtocolFigureDeltaHR,
    ProtocolFigureConnMap,
    ProtocolFigureSpectrum,
    ProtocolFigureImpedance
} from "../types/dtos/charts";
import {BiomarkerNames, BiomarkerColumnNames} from "../types/biomarkerNames";
import {ChartTypes} from "../dashboards/charts";

export const loadProtocolDefStats = async (companyId: TCompanyId): Promise<Array<IProtocolDefStats>> => {
    const url = `/companies/${companyId}/protocols/defs/stats`
    const res: AxiosResponse<IStandardPagedResponse<IProtocolDefStatsDTO>> = await apiClient.get(url,
        {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Definitions Statistics failed."})
        } as RetryConfig)
    return res.data.data.map((dto) => ({
        pdefId: dto.pdef_id,
        name: dto.name,
        sessionCount: dto.session_count,
        lastSessionStart: dto.last_session_start ? dayjs(dto.last_session_start) : undefined,
        stimCount: dto.stim_count,
        megScanCount: dto.meg_scan_count,
        eegScanCount: dto.eeg_scan_count,
        ecgScanCount: dto.ecg_scan_count,
        totalStimSeconds: dto.total_stim_seconds,
        totalMEGSeconds: dto.total_meg_seconds,
        totalEEGSeconds: dto.total_eeg_seconds,
        numUniqueSubjects: dto.num_unique_subjects,
        completedCount: dto.completed_count,
    }))
}
export const loadProtocolStats = async (companyId: TCompanyId, pdefId: TProtocolDefinitionId): Promise<Array<IProtocolStats>> => {
    const url = `/companies/${companyId}/protocols/stats`
    const res: AxiosResponse<IStandardPagedResponse<IProtocolStatsDTO>> = await apiClient.get(url,
        {
            params: {pdef_id: pdefId},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Statistics failed."})
        } as RetryConfig)
    return res.data.data.map((dto) => ({
        id: dto.id,
        pdefId: dto.pdef_id,
        name: dto.name,
        startTime: dayjs(dto.start_time),
        subjectId: dto.subject_id,
        subjectName: dto.subject_name,
        stimCount: dto.stim_count,
        megScanCount: dto.meg_scan_count,
        eegScanCount: dto.eeg_scan_count,
        ecgScanCount: dto.ecg_scan_count,
        totalStimSeconds: dto.total_stim_seconds,
        totalMEGSeconds: dto.total_meg_seconds,
        totalEEGSeconds: dto.total_eeg_seconds,
    }))
}

export const loadProtocolHRHRVData = async (companyId: TCompanyId, protocol : IProtocol): Promise<ProtocolFigureHrHrv> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/hr_hrv`
    const res: AxiosResponse<ProtocolFigureHrHrv> = await apiClient.get(url,
        {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Hr/Hrv chart failed."})
        } as RetryConfig)
    return res.data
}

export const loadProtocolRMSSNRData = async (companyId: TCompanyId, protocol : IProtocol): Promise<ProtocolFigureRmsSnr> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/rms_snr`
    const res: AxiosResponse<ProtocolFigureRmsSnr> = await apiClient.get(url,
        {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol RMS+SNR chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolSpikeCountsData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
    threshold?: number | null,
    time_start?: string | null,
    time_end?: string | null,
): Promise<ProtocolFigureSpikeCounts> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/spike_counts`
    const res: AxiosResponse<ProtocolFigureSpikeCounts> = await apiClient.get(url,
        {
            params: {threshold, time_start, time_end},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Spike Counts chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolSpikesData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
    limit?: number | null,
    offset?: number | null,
    threshold?: number | null,
    channel_name?: string | null,
    time_start?: string | null,
    time_end?: string | null,
): Promise<ProtocolFigureSpikes> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/spikes`
    const res: AxiosResponse<ProtocolFigureSpikes> = await apiClient.get(url,
        {
            params: {limit, offset, threshold, channel_name, time_start, time_end},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Spikes chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolDAQStepBounds = async (
    companyId: TCompanyId,
    protocol : IProtocol,
): Promise<{ start: string | null; end: string | null }> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/daq_step_bounds`
    const res: AxiosResponse<IStandardResponse<[string | null, string | null]>> = await apiClient.get(url,
        {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading DAQ step bounds failed."})
        } as RetryConfig)
    const pair = res.data.data || [null, null]
    const start = pair[0]
    const end = pair[1]
    return { start, end }
}


export const loadProtocolDeltaConnData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
    biomarker: BiomarkerNames,
    column_names: string[],
    frequency_bands: string[],
    channel_names: string[],
): Promise<ProtocolFigureDeltaConn> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/delta_biomarker`
    const res: AxiosResponse<ProtocolFigureDeltaConn> = await apiClient.get(url,
        {
            params: {biomarker, column_names, frequency_bands, channel_names},
            paramsSerializer: {indexes: null},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Delta Connectivity chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolConnDistData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
    biomarker: BiomarkerNames,
    column_names: string[],
    frequency_bands: string[],
    channel_names: string[],
): Promise<ProtocolFigureConnDist> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/biomarker_distribution`
    const res: AxiosResponse<ProtocolFigureConnDist> = await apiClient.get(url,
        {
            params: {biomarker, column_names, frequency_bands, channel_names},
            paramsSerializer: {indexes: null},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Connectivity Distribution chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolConnMapData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
    biomarker: BiomarkerNames,
    frequency_bands: string[],
): Promise<ProtocolFigureConnMap> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/biomarker_map`
    const res: AxiosResponse<ProtocolFigureConnMap> = await apiClient.get(url,
        {
            params: {biomarker, frequency_bands},
            paramsSerializer: {indexes: null},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Connectivity Map chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolDeltaHRData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
    biomarker: BiomarkerNames,
    column_names: string[],
): Promise<ProtocolFigureDeltaHR> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/delta_biomarker`
    const res: AxiosResponse<ProtocolFigureDeltaHR> = await apiClient.get(url,
        {
            params: {biomarker, column_names},
            paramsSerializer: {indexes: null},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Delta HR chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolBiomarkerNames = async (companyId: TCompanyId, protocol : IProtocol, column_names, require_all_columns): Promise<{
    biomarker_names: string[];
}> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/biomarker_names`
    const res: AxiosResponse<{ biomarker_names: string[] }> = await apiClient.get(url, {
        params: {column_names, require_all_columns},
        paramsSerializer: {indexes: null},
        retry: true,
        maxRetries: 5,
        backoff: 300,
        onRetry: createRetryingNotificationHandler({ message: "Loading biomarker names failed." })
    } as RetryConfig)

    return res.data
}


export const loadProtocolBiomarkerOptions = async (companyId: TCompanyId, protocol : IProtocol, biomarker: BiomarkerNames, column_name: BiomarkerColumnNames): Promise<{
    options: string[];
}> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/biomarker_options`
    const res: AxiosResponse<{ options: string[] }> = await apiClient.get(url, {
        params: {biomarker, column_name},
        retry: true,
        maxRetries: 5,
        backoff: 300,
        onRetry: createRetryingNotificationHandler({ message: "Loading Options for Biomarker failed." })
    } as RetryConfig)

    return res.data
}


export const loadProtocolBiomarkerData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
    figure_name: ChartTypes,
    biomarker: BiomarkerNames,
    column_names: BiomarkerColumnNames[],
    frequency_bands: string[],
    channel_names: string[],
): Promise<T> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/biomarker`
    const res: AxiosResponse<T> = await apiClient.get(url,
        {
            params: {figure_name, biomarker, column_names, frequency_bands, channel_names},
            paramsSerializer: {indexes: null},
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol biomarker chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolSpectrumData = async (
    companyId: TCompanyId,
    protocol : IProtocol,
): Promise<ProtocolFigureSpectrum> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/spectrum`
    const res: AxiosResponse<ProtocolFigureSpectrum> = await apiClient.get(url,
        {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Spectrum chart failed."})
        } as RetryConfig)
    return res.data
}


export const loadProtocolImpedanceData = async (
    companyId: TCompanyId,
    protocol: IProtocol,
): Promise<ProtocolFigureImpedance> => {
    const url = `/companies/${companyId}/protocols/${protocol.id}/figures/impedance`
    const res: AxiosResponse<ProtocolFigureImpedance> = await apiClient.get(url,
        {
            retry: true,
            maxRetries: 5,
            backoff: 300,
            onRetry: createRetryingNotificationHandler({message: "Loading Protocol Impedance chart failed."})
        } as RetryConfig)
    return res.data
}
